import { useState, type FormEvent } from 'react';
import { MessageCircle, ShieldCheck, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../hooks/useAuth';

export default function AuthScreen({ mode }: { mode: 'auth' | 'missing-config' }) {
  const { signIn, signUp, authMessage } = useAuth();
  const [view, setView] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      if (view === 'login') {
        await signIn(email, password);
      } else {
        await signUp({ email, password, username, displayName });
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen overflow-x-hidden overflow-y-auto bg-ink-950 px-4 py-8 text-slate-100">
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl items-center gap-8 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="min-w-0 space-y-8">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-relay-cyan shadow-glow">
            <MessageCircle size={30} />
          </div>
          <div className="max-w-2xl space-y-5">
            <h1 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
              Relay is your real-time team and friend chat hub.
            </h1>
            <p className="text-lg leading-8 text-slate-300">
              Direct messages, group chats, uploads, friend requests, live presence, typing signals,
              and an admin dashboard on Supabase and Cloudflare Pages.
            </p>
          </div>
          <div className="grid max-w-2xl gap-3 sm:grid-cols-3">
            {[
              ['Supabase Auth', 'Email/password accounts with protected profile rows.'],
              ['Realtime chat', 'Postgres changes, presence, and broadcast typing.'],
              ['Cloudflare Pages', 'Static Vite build deployed from dist.'],
            ].map(([title, copy]) => (
              <div key={title} className="rounded-lg border border-white/10 bg-white/[0.04] p-4">
                <Sparkles className="mb-3 text-relay-cyan" size={18} />
                <h2 className="text-sm font-semibold text-white">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-400">{copy}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="min-w-0 rounded-xl border border-white/10 bg-ink-850/95 p-5 shadow-panel backdrop-blur">
          {mode === 'missing-config' ? (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <ShieldCheck className="text-relay-cyan" />
                <div>
                  <h2 className="text-xl font-semibold text-white">Connect Supabase</h2>
                  <p className="text-sm text-slate-400">Environment variables are missing.</p>
                </div>
              </div>
              <div className="rounded-lg border border-relay-cyan/20 bg-relay-cyan/10 p-4 text-sm leading-7 text-slate-200">
                Create a <code className="rounded bg-black/30 px-1.5 py-0.5">.env.local</code> file
                from <code className="rounded bg-black/30 px-1.5 py-0.5">.env.example</code> and add:
                <pre className="mt-3 whitespace-pre-wrap break-all rounded-lg bg-black/40 p-3 text-xs text-slate-200">
                  VITE_SUPABASE_URL=https://your-project-ref.supabase.co{'\n'}
                  VITE_SUPABASE_ANON_KEY=your-anon-key
                </pre>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-5 grid grid-cols-2 rounded-lg border border-white/10 bg-black/20 p-1">
                {(['login', 'register'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setView(tab)}
                    className={clsx(
                      'rounded-md px-4 py-2.5 text-sm font-semibold transition',
                      view === tab ? 'bg-white text-ink-950' : 'text-slate-400 hover:text-white',
                    )}
                  >
                    {tab === 'login' ? 'Log in' : 'Register'}
                  </button>
                ))}
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                {view === 'register' && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-sm font-medium text-slate-300">Username</span>
                      <input
                        value={username}
                        onChange={(event) => setUsername(event.target.value)}
                        minLength={3}
                        maxLength={24}
                        pattern="[a-zA-Z0-9_]+"
                        required
                        className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                        placeholder="jacob"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-slate-300">Display name</span>
                      <input
                        value={displayName}
                        onChange={(event) => setDisplayName(event.target.value)}
                        required
                        className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                        placeholder="Jacob"
                      />
                    </label>
                  </div>
                )}
                <label className="block">
                  <span className="text-sm font-medium text-slate-300">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                    placeholder="you@example.com"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-slate-300">Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    minLength={8}
                    required
                    className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-slate-500"
                    placeholder="At least 8 characters"
                  />
                </label>

                {(error || authMessage) && (
                  <div className="rounded-lg border border-relay-red/30 bg-relay-red/10 px-4 py-3 text-sm text-rose-100">
                    {error || authMessage}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-gradient-to-r from-relay-blue to-relay-violet px-5 py-3 text-sm font-bold text-white shadow-glow transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Working...' : view === 'login' ? 'Log in to Relay' : 'Create account'}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </main>
  );
}
